import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import debounce from 'lodash.debounce';

import useIsMobileView from 'hooks/useIsMobileView';
import { updateTableColumnWidths } from 'common/pagination';
import { useUserDetailsContext } from 'context/userDetailsContext';

const MIN_WIDTH_PERCENT = 5; // minimum width in %

const normalizeWidths = (widths, columns) => {
  const nonFixedPercentCols = columns?.filter(
    (col) => !col.fixed && (!col.width || !col.width.includes('px')) && col.field && !col.selectionMode
  );

  const total = nonFixedPercentCols.reduce((sum, col) => {
    const width = parseFloat(widths?.[col.field]) || 100 / columns?.length;
    return sum + width;
  }, 0);

  const factor = 100 / total;
  const normalized = {};

  columns?.forEach((col) => {
    // Skip selection columns (they don't have a field)
    if (col.selectionMode || !col.field) {
      return;
    }

    const saved = widths?.[col?.field];

    if (col.fixed && col.width?.includes('px')) {
      normalized[col?.field] = col.width;
    } else if (col?.fixed) {
      normalized[col?.field] = col?.width || saved || 'auto';
    } else {
      let width = parseFloat(saved) || 100 / columns?.length;
      width = width * factor;
      width = Math.max(width, MIN_WIDTH_PERCENT);
      normalized[col?.field] = width.toFixed(2) + '%';
    }
  });

  return normalized;
};

const ResizableDataTable = ({ value = [], columns = [], storageKey = 'default_table_column_widths', ...rest }) => {
  const userDetails = useUserDetailsContext();
  const isMobileView = useIsMobileView();
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);
  const _columns = columns.filter((col) => !col.hidden);

  useEffect(() => {
    if (rest?.resizableColumns !== false) {
      const savedWidths = userDetails?.firmDetails?.user_preferences?.table_column?.[storageKey] || {};
      const normalized = normalizeWidths(savedWidths, _columns);
      setColumnWidths(normalized);
    }
  }, [storageKey]);

  const debouncedSave = useRef(
    debounce((newWidths) => {
      updateTableColumnWidths(userDetails, storageKey, newWidths);
    }, 1500)
  ).current;

  const onColumnResizeEnd = (event) => {
    const resizedField = event.column.field;
    if (!resizedField) return; // Skip if no field (e.g., selection column)

    const resizedCol = _columns.find((c) => c.field === resizedField);

    // Prevent resizing fixed px-width or resizable: false columns
    if (resizedCol?.fixed && resizedCol?.width?.includes('px')) return;
    if (resizedCol?.resizable === false) return;

    const tableWidth = tableRef.current?.offsetWidth || 1;
    let newResizedWidth = (event.element.offsetWidth / tableWidth) * 100;
    newResizedWidth = Math.max(newResizedWidth, MIN_WIDTH_PERCENT);

    const updatedWidths = {};

    const fixedPxWidth = _columns.reduce((sum, col) => {
      const w = col.fixed && col.width?.includes('px') ? parseFloat(col.width) : 0;
      return sum + w;
    }, 0);

    const remainingPercentageWidth = 100 - (fixedPxWidth / tableWidth) * 100 - newResizedWidth;

    const otherResizableCols = _columns.filter(
      (col) =>
        col.field &&
        col.field !== resizedField &&
        (!col.fixed || !col.width?.includes('px')) &&
        col.resizable !== false &&
        !col.selectionMode
    );

    const totalOtherWidth = otherResizableCols.reduce((sum, col) => {
      return sum + (parseFloat(columnWidths[col.field]) || 100 / _columns.length);
    }, 0);

    _columns.forEach((col) => {
      // Skip selection columns (they don't have a field)
      if (col.selectionMode || !col.field) {
        return;
      }

      if (col.fixed && col.width?.includes('px')) {
        updatedWidths[col.field] = col.width;
      } else if (col.resizable === false) {
        updatedWidths[col.field] = col.width || columnWidths[col.field] || 'auto';
      } else if (col.field === resizedField) {
        updatedWidths[col.field] = newResizedWidth.toFixed(2) + '%';
      } else {
        const currentWidth = parseFloat(columnWidths[col.field]) || 100 / _columns.length;
        const adjustedWidth = (currentWidth / totalOtherWidth) * remainingPercentageWidth;
        updatedWidths[col.field] = Math.max(adjustedWidth, MIN_WIDTH_PERCENT).toFixed(2) + '%';
      }
    });

    const normalized = normalizeWidths(updatedWidths, _columns);
    setColumnWidths(normalized);
    debouncedSave(normalized);

    setTimeout(() => {
      const table = tableRef.current;
      if (!table) return;

      _columns.forEach((col, idx) => {
        if (col.fixed && col.width?.includes('px')) {
          const th = table.querySelector(`.p-datatable-thead th:nth-child(${idx + 1})`);
          if (th) {
            th.style.width = col.width;
            th.style.minWidth = col.width;
            th.style.maxWidth = col.width;
          }
        }
      });
    }, 0);
  };

  return (
    <div className="datatable-responsive" ref={tableRef}>
      <DataTable
        value={value}
        resizableColumns
        columnResizeMode="fit"
        onColumnResizeEnd={onColumnResizeEnd}
        {...rest}
        className="p-datatable-responsive w-100"
      >
        {_columns.map((col, index) => {
          const { field, header, resizable, style = {}, selectionMode, ...restProps } = col;
          const isSelectionColumn = !!selectionMode;
          const width = isMobileView
            ? { ...style }
            : {
                width:
                  col.fixed && col.width ? col.width : (isSelectionColumn ? col.width : columnWidths[col.field] || col.width) || 'auto',
                ...style,
              };
          const className = resizable === false || (col.fixed && col.width?.includes('px')) ? 'no-resize' : '';

          const columnProps = isSelectionColumn
            ? {
                selectionMode,
                header,
                ...restProps,
              }
            : {
                field,
                header,
                ...restProps,
              };

          return (
            <Column
              key={isSelectionColumn ? `selection-${index}` : `${field}-${index}`}
              {...columnProps}
              className={className + ' ' + col?.className}
              style={width}
            />
          );
        })}
      </DataTable>
    </div>
  );
};

export default ResizableDataTable;
